import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';

export function MfaEnroll() {
  const [busy, setBusy] = useState(false);
  const [enrollment, setEnrollment] = useState<{ secret: string; otpAuth: string; backupCodes: string[] } | null>(null);
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: () => api('/api/mfa/verify', { method: 'POST', body: JSON.stringify({ code }) }),
    onSuccess: () => setVerified(true),
    onError: (e: { message: string }) => setErr(e.message),
  });

  async function startEnroll() {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ secret: string; otpAuth: string; backupCodes: string[] }>('/api/mfa/enroll', { method: 'POST' });
      setEnrollment(r);
    } finally { setBusy(false); }
  }

  if (verified) {
    return (
      <div className="max-w-xl mx-auto py-12">
        <p className="qa-eyebrow mb-2">MFA enabled</p>
        <h1 className="qa-h1">All set — <span className="qa-italic-accent">securely.</span></h1>
        <p className="qa-deck mt-3 text-ink-soft">Multi-factor authentication is now active on your account.</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-8">
      <p className="qa-eyebrow mb-2">Enable two-factor authentication</p>
      <h1 className="qa-h1">Hard-lock your <span className="qa-italic-accent">account.</span></h1>
      {!enrollment ? (
        <div className="qa-card p-6 mt-6">
          <p className="text-ink-mute mb-4">Add a TOTP authenticator app (1Password, Authy, Google Authenticator) for a second factor at sign-in.</p>
          <button className="btn-primary" onClick={startEnroll} disabled={busy}>{busy ? 'Generating…' : 'Generate secret'}</button>
        </div>
      ) : (
        <div className="qa-card p-6 mt-6 space-y-4">
          <div>
            <p className="qa-label">Manual entry</p>
            <p className="qa-mono text-sm tracking-wider break-all">{enrollment.secret}</p>
          </div>
          <div>
            <p className="qa-label">otpauth:// URL (paste into your authenticator)</p>
            <textarea className="qa-textarea text-xs qa-mono" readOnly value={enrollment.otpAuth} />
          </div>
          <div className="bg-parchment border border-hairline-paper p-4">
            <p className="qa-eyebrow mb-2">Backup codes</p>
            <p className="text-xs text-ink-mute mb-2">Store these somewhere safe. Each is single-use.</p>
            <ul className="grid grid-cols-2 gap-2 qa-mono text-sm">
              {enrollment.backupCodes.map(c => <li key={c}>{c}</li>)}
            </ul>
          </div>
          <div>
            <label className="qa-label">Enter the 6-digit code from your app</label>
            <input className="qa-input qa-mono text-center text-lg tracking-widest" maxLength={6}
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          {err && <div className="qa-badge qa-badge--out">{err}</div>}
          <button className="btn-primary w-full" disabled={code.length !== 6 || verify.isPending} onClick={() => verify.mutate()}>
            {verify.isPending ? 'Verifying…' : 'Verify and enable'}
          </button>
        </div>
      )}
    </div>
  );
}
